import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTenant } from '@/contexts'
import {
  fetchLeases,
  fetchLeaseById,
  fetchContractAircraft,
  createLease,
  updateLease,
  deleteLease,
  restoreLease,
  terminateLease,
  cancelLease,
  completeLease,
} from '@/lib/api/leases'
import { broadcastInvalidation } from '@/lib/sync'
import type { LeaseFormData, LeaseFilters } from '@/types/leases'
import { toast } from 'sonner'

/**
 * Query key factory for leases
 * Provides consistent keys for cache management
 */
export const leaseKeys = {
  all: ['leases'] as const,
  lists: () => [...leaseKeys.all, 'list'] as const,
  list: (organizationId: string, filters?: LeaseFilters) =>
    [...leaseKeys.lists(), organizationId, filters] as const,
  details: () => [...leaseKeys.all, 'detail'] as const,
  detail: (id: string) => [...leaseKeys.details(), id] as const,
  aircraft: (leaseId: string) => [...leaseKeys.all, 'aircraft', leaseId] as const,
}

/**
 * Fetch all leases for current organization with optional filtering
 */
export function useLeases(filters?: LeaseFilters) {
  const { currentOrganization } = useTenant()
  const organizationId = currentOrganization?.id

  return useQuery({
    queryKey: leaseKeys.list(organizationId || '', filters),
    queryFn: () => fetchLeases(organizationId!, filters),
    enabled: !!organizationId,
    refetchOnWindowFocus: true,
  })
}

/**
 * Fetch a single lease by ID
 */
export function useLeaseById(id: string | undefined) {
  return useQuery({
    queryKey: leaseKeys.detail(id || ''),
    queryFn: () => fetchLeaseById(id!),
    enabled: !!id,
  })
}

/**
 * Fetch aircraft linked to a lease
 */
export function useContractAircraft(leaseId: string | undefined) {
  return useQuery({
    queryKey: leaseKeys.aircraft(leaseId || ''),
    queryFn: () => fetchContractAircraft(leaseId!),
    enabled: !!leaseId,
  })
}

/**
 * Create a new lease
 */
export function useCreateLease() {
  const queryClient = useQueryClient()
  const { currentOrganization } = useTenant()

  return useMutation({
    mutationFn: async ({
      data,
      aircraftIds,
      primaryAircraftId,
    }: {
      data: LeaseFormData
      aircraftIds?: string[]
      primaryAircraftId?: string | null
    }) => {
      return createLease(currentOrganization!.id, data, aircraftIds, primaryAircraftId)
    },
    onSuccess: () => {
      const keys = [leaseKeys.lists(), ['map-allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Lease contract created successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to create lease contract', {
        description: error.message,
      })
    },
  })
}

/**
 * Update an existing lease
 */
export function useUpdateLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      id,
      data,
      aircraftIds,
      primaryAircraftId,
    }: {
      id: string
      data: Partial<LeaseFormData>
      aircraftIds?: string[]
      primaryAircraftId?: string | null
    }) => {
      return updateLease(id, data, aircraftIds, primaryAircraftId)
    },
    onSuccess: (_, { id }) => {
      const keys = [leaseKeys.lists(), leaseKeys.detail(id), leaseKeys.aircraft(id), ['map-allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Lease contract updated successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to update lease contract', {
        description: error.message,
      })
    },
  })
}

/**
 * Delete a lease (soft delete)
 */
export function useDeleteLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => deleteLease(id),
    onSuccess: () => {
      const keys = [leaseKeys.lists(), ['map-allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Lease contract deleted successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to delete lease contract', {
        description: error.message,
      })
    },
  })
}

/**
 * Restore a soft-deleted lease
 */
export function useRestoreLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => restoreLease(id),
    onSuccess: () => {
      const keys = [leaseKeys.lists(), ['map-allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Lease contract restored successfully')
    },
    onError: (error: Error) => {
      toast.error('Failed to restore lease contract', {
        description: error.message,
      })
    },
  })
}

/**
 * Terminate a lease (early exit / eviction)
 */
export function useTerminateLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, ...params }: { id: string; termination_reason: string; termination_date: string }) =>
      terminateLease(id, params),
    onSuccess: (_, { id }) => {
      const keys = [leaseKeys.lists(), leaseKeys.detail(id), ['map-allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Lease terminated')
    },
    onError: (error: Error) => {
      toast.error('Failed to terminate lease', { description: error.message })
    },
  })
}

/**
 * Cancel a lease (draft / pending abandoned)
 */
export function useCancelLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, cancellation_reason }: { id: string; cancellation_reason?: string }) =>
      cancelLease(id, cancellation_reason ? { cancellation_reason } : undefined),
    onSuccess: (_, { id }) => {
      const keys = [leaseKeys.lists(), leaseKeys.detail(id), ['map-allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Lease cancelled')
    },
    onError: (error: Error) => {
      toast.error('Failed to cancel lease', { description: error.message })
    },
  })
}

/**
 * Complete a lease (happy-path end of term)
 */
export function useCompleteLease() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => completeLease(id),
    onSuccess: (_, id) => {
      const keys = [leaseKeys.lists(), leaseKeys.detail(id), ['map-allocations']] as const
      keys.forEach((k) => queryClient.invalidateQueries({ queryKey: [...k] }))
      broadcastInvalidation(keys)
      toast.success('Lease marked as completed')
    },
    onError: (error: Error) => {
      toast.error('Failed to complete lease', { description: error.message })
    },
  })
}
